import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { loadHyper } from '@juspay-tech/hyper-js';
import { HyperElements, UnifiedCheckout } from '@juspay-tech/react-hyper-js';
import { ChevronRight, CreditCard, Loader2, Plus, Receipt } from 'lucide-react';
import { dtpApi } from '../../lib/dtpApi';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { getCurrentTenantConfig } from '../../config/tenant';
import { formatErrorForDisplay, parseRedirectError } from '../../lib/hyperswitchErrors';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { cardBrandLabel, formatDate, formatMoney, PatientCard, ReceiptSummary, selfServiceError } from '../../lib/patientSelfService';
import { track } from '../../lib/analytics';
import { useTrackWhen } from '../../components/analytics/useTrackWhen';

/** Saved cards and receipts for this clinic. */
export function DtpBilling() {
  return <div className="mx-auto max-w-3xl">
    <h1 className="portal-display text-[36px] font-bold leading-none sm:text-[44px]">Billing</h1>
    <p className="mt-3 text-[17px] text-[var(--portal-muted)]">Your saved cards and every payment you’ve made with this clinic.</p>
    <PaymentMethods />
    <Receipts />
  </div>;
}

const REDIRECT_PARAMS = ['redirect_status', 'error_code', 'error_message', 'payment_id', 'payment_intent_client_secret', 'status'];

function PaymentMethods() {
  const { physicianId } = useDtpParams();
  const patientId = useDtpAuthStore(s => s.patient?._id);
  const impersonated = useDtpAuthStore(s => s.impersonated);
  const [searchParams, setSearchParams] = useSearchParams();
  // After the SDK's return_url round-trip the card is saved by the payment
  // webhook, a moment after the redirect. Poll briefly so it shows up.
  const [verifying, setVerifying] = useState(false);
  const cards = useQuery({
    queryKey: ['patient-cards', physicianId, patientId],
    retry: false,
    gcTime: 0,
    queryFn: () => dtpApi.getPaymentMethods(),
    refetchInterval: verifying ? 3000 : false,
  });
  const [adding, setAdding] = useState<{ clientSecret: string; hyper: unknown } | null>(null);
  const [startingAdd, setStartingAdd] = useState(false);
  const [busyCard, setBusyCard] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  useTrackWhen(cards.isSuccess, 'billing_viewed', { cardCount: cards.data?.paymentMethods?.length ?? 0 });

  useEffect(() => {
    const status = searchParams.get('redirect_status');
    if (!status) return;
    const clean = new URLSearchParams(searchParams);
    REDIRECT_PARAMS.forEach(p => clean.delete(p));
    if (status === 'succeeded' || status === 'processing') {
      setNotice('Your card is being verified. It will appear here in a moment. The $1 verification charge is refunded automatically.');
      setVerifying(true);
    } else {
      const parsed = parseRedirectError(searchParams);
      setError((parsed && formatErrorForDisplay(parsed).description) || 'Your card couldn’t be saved. Please try a different card.');
    }
    setSearchParams(clean, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!verifying) return;
    const stop = setTimeout(() => setVerifying(false), 30_000);
    return () => clearTimeout(stop);
  }, [verifying]);

  async function startAdd() {
    track('card_add_started');
    setStartingAdd(true); setError(''); setNotice('');
    try {
      const { clientSecret } = await dtpApi.startAddPaymentMethod();
      const cfg = getCurrentTenantConfig();
      const customBackendUrl = cfg.payments.hyperswitch.environment === 'sandbox' ? 'https://sandbox.hyperswitch.io' : 'https://api.hyperswitch.io';
      setAdding({ clientSecret, hyper: loadHyper(cfg.payments.hyperswitch.publishableKey, { customBackendUrl }) });
    } catch (err) {
      setError(selfServiceError(err, 'We couldn’t start adding a card. Please try again.'));
    } finally { setStartingAdd(false); }
  }

  async function act(card: PatientCard, action: () => Promise<void>, done: string) {
    setBusyCard(card.id); setError(''); setNotice('');
    try { await action(); setNotice(done); setConfirmRemove(null); await cards.refetch(); }
    catch (err) { setError(selfServiceError(err)); }
    finally { setBusyCard(null); }
  }

  const list: PatientCard[] = cards.data?.paymentMethods || [];
  return <section aria-labelledby="payment-methods" className="portal-card mt-8 p-6 sm:p-8">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="grid h-9 w-9 place-items-center rounded-xl bg-[var(--portal-tint-1)]"><CreditCard className="h-[18px] w-[18px]" /></span>
        <h2 id="payment-methods" className="portal-display text-xl font-semibold">Payment methods</h2>
      </div>
      {!impersonated && cards.data?.canAddCard && !adding && <button type="button" onClick={startAdd} disabled={startingAdd}
        className="inline-flex h-10 items-center gap-2 rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] transition hover:opacity-90 disabled:opacity-60">
        {startingAdd ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : <Plus aria-hidden="true" className="h-4 w-4" />}Add a card
      </button>}
    </div>

    {cards.isPending ? <p role="status" className="mt-5 text-sm text-[var(--portal-muted)]">Loading your cards…</p>
      : cards.isError ? <div role="alert" className="mt-5 rounded-2xl bg-[#fde4dc] p-4 text-sm text-[#8c2a14]">We couldn’t load your cards. <button className="min-h-[44px] font-semibold underline" onClick={() => cards.refetch()}>Try again</button></div>
      : <>
        {list.length === 0 ? <div className="mt-5 rounded-2xl border border-dashed border-[rgba(36,26,21,0.16)] px-6 py-10 text-center"><p className="portal-display text-lg font-semibold">No saved cards</p><p className="mt-1 text-sm text-[var(--portal-muted)]">Add one to pay for orders and refills in a tap.</p></div>
          : <ul className="mt-5 divide-y divide-[var(--portal-line)] rounded-2xl border border-[var(--portal-line)] bg-white/70">
            {list.map(card => <li key={card.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-[15px] font-medium">{cardBrandLabel(card.brand)} •••• {card.last4 || '····'}</p>
                <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-[var(--portal-muted)]">
                  {card.expMonth && card.expYear && <span className={isExpired(card) ? 'font-semibold text-[#8c2a14]' : ''}>{isExpired(card) ? 'Expired' : 'Expires'} {String(card.expMonth).padStart(2, '0')}/{String(card.expYear).slice(-2)}</span>}
                  {card.isDefault && <span className="rounded-full bg-[var(--portal-tint-2)] px-2 py-0.5 font-semibold text-[var(--portal-ink)]">Default</span>}
                  {card.usedByMembership && <span className="rounded-full bg-[var(--portal-tint-1)] px-2 py-0.5 font-semibold text-[var(--portal-ink)]">Membership card</span>}
                </p>
              </div>
              {!impersonated && (confirmRemove === card.id ? <div className="flex items-center gap-2">
                <span className="text-sm">Remove this card?</span>
                <button type="button" disabled={busyCard !== null} onClick={() => act(card, () => dtpApi.removePaymentMethod(card.id), 'Card removed.')}
                  className="inline-flex h-10 items-center gap-1.5 rounded-full bg-[#8c2a14] px-4 text-sm font-semibold text-white disabled:opacity-60">{busyCard === card.id && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}Remove</button>
                <button type="button" onClick={() => setConfirmRemove(null)} className="h-10 px-2 text-sm text-[var(--portal-muted)] underline">Cancel</button>
              </div> : <div className="flex items-center gap-1">
                {!card.isDefault && <button type="button" disabled={busyCard !== null} onClick={() => act(card, () => dtpApi.setDefaultPaymentMethod(card.id), 'Default card updated.')}
                  className="h-10 rounded-full px-3 text-sm font-medium text-[var(--portal-ink)] hover:bg-[rgba(36,26,21,0.05)] disabled:opacity-50">Make default</button>}
                {card.usedByMembership
                  ? <Link to={dtpPath(physicianId, '/membership')} className="inline-flex h-10 items-center rounded-full px-3 text-sm font-medium text-[var(--portal-ink)] hover:bg-[rgba(36,26,21,0.05)]">Replace in Membership</Link>
                  : <button type="button" disabled={busyCard !== null} onClick={() => { setConfirmRemove(card.id); setError(''); setNotice(''); }}
                    aria-label={`Remove ${cardBrandLabel(card.brand)} ending ${card.last4 || ''}`}
                    className="h-10 rounded-full px-3 text-sm font-medium text-[var(--portal-muted)] hover:bg-[rgba(36,26,21,0.05)] hover:text-[#8c2a14] disabled:opacity-50">Remove</button>}
              </div>)}
            </li>)}
          </ul>}
        {list.some(c => c.usedByMembership) && <p className="mt-3 text-sm text-[var(--portal-muted)]">Your membership is billed to its own card. To change it, replace the card in Membership.</p>}
        {!impersonated && cards.data && !cards.data.canAddCard && <p className="mt-3 text-sm text-[var(--portal-muted)]">To save a card, first <Link className="underline" to={dtpPath(physicianId, '/account')}>add an email address</Link> to your account.</p>}
      </>}

    {adding && <div className="mt-6 rounded-2xl border border-[var(--portal-line)] bg-white p-4 sm:p-5">
      <p className="text-sm text-[var(--portal-muted)]">To check the card, we charge $1 and refund it automatically.</p>
      <div className="mt-4">
        <HyperElements hyper={adding.hyper as any} options={{ publishableKey: getCurrentTenantConfig().payments.hyperswitch.publishableKey, clientSecret: adding.clientSecret, loader: 'auto' } as any}>
          <UnifiedCheckout id="dtp-billing-add-card" options={{
            layout: { type: 'tabs' },
            paymentMethodOrder: ['card'],
            sdkHandleConfirmPayment: { handleConfirm: true, buttonText: 'Save card', confirmParams: { return_url: window.location.href.split('?')[0] } },
          } as any} />
        </HyperElements>
      </div>
      <button type="button" onClick={() => setAdding(null)} className="mt-3 h-10 text-sm text-[var(--portal-muted)] underline">Cancel</button>
    </div>}

    <div aria-live="polite" className="mt-3 text-sm">
      {error ? <p role="alert" className="rounded-lg bg-red-50 p-3 text-red-700">{error}</p> : notice ? <p className="text-green-700">{notice}</p> : null}
    </div>
  </section>;
}

function isExpired(card: PatientCard, now = new Date()) {
  if (!card.expMonth || !card.expYear) return false;
  return card.expYear < now.getFullYear() || (card.expYear === now.getFullYear() && card.expMonth < now.getMonth() + 1);
}

function Receipts() {
  const { physicianId } = useDtpParams();
  const patientId = useDtpAuthStore(s => s.patient?._id);
  const receipts = useQuery({ queryKey: ['patient-receipts', physicianId, patientId], retry: false, gcTime: 0, queryFn: () => dtpApi.getReceipts() });
  return <section aria-labelledby="receipts" className="portal-card mt-5 p-6 sm:p-8">
    <div className="flex items-center gap-3">
      <span aria-hidden="true" className="grid h-9 w-9 place-items-center rounded-xl bg-[var(--portal-tint-3)]"><Receipt className="h-[18px] w-[18px]" /></span>
      <h2 id="receipts" className="portal-display text-xl font-semibold">Receipts</h2>
    </div>
    {receipts.isPending ? <div role="status" aria-label="Loading receipts" className="mt-5 space-y-2">{[0, 1].map(i => <div key={i} className="h-16 rounded-2xl bg-[rgba(36,26,21,0.05)] motion-safe:animate-pulse" />)}</div>
      : receipts.isError ? <div role="alert" className="mt-5 rounded-2xl bg-[#fde4dc] p-4 text-sm text-[#8c2a14]">We couldn’t load your receipts. <button className="min-h-[44px] font-semibold underline" onClick={() => receipts.refetch()}>Try again</button></div>
      : receipts.data.length === 0 ? <div className="mt-5 rounded-2xl border border-dashed border-[rgba(36,26,21,0.16)] px-6 py-10 text-center"><p className="portal-display text-lg font-semibold">No payments yet</p><p className="mt-1 text-sm text-[var(--portal-muted)]">Receipts for orders and membership renewals appear here.</p></div>
      : <ul className="mt-5 divide-y divide-[var(--portal-line)] overflow-hidden rounded-2xl border border-[var(--portal-line)] bg-white/70">{receipts.data.map((r: ReceiptSummary) => <li key={r.id}><ReceiptRow receipt={r} /></li>)}</ul>}
  </section>;
}

function ReceiptRow({ receipt }: { receipt: ReceiptSummary }) {
  const { physicianId } = useDtpParams();
  return <Link to={dtpPath(physicianId, `/billing/receipts/${receipt.id}`)} aria-label={`Receipt: ${receipt.description}, ${formatMoney(receipt.amountPaid)}, ${formatDate(receipt.paidAt)}`}
    className="group flex items-center justify-between gap-4 px-4 py-3.5 transition hover:bg-white">
    <span className="min-w-0">
      <span className="block truncate text-[15px] font-medium">{receipt.description}</span>
      <span className="mt-0.5 block text-xs text-[var(--portal-muted)]">{formatDate(receipt.paidAt)} · #{receipt.orderNumber}{receipt.card ? ` · ${cardBrandLabel(receipt.card.brand)} •••• ${receipt.card.last4}` : ''}</span>
      {receipt.refund && <span className="mt-2 inline-flex rounded-full bg-[#fff1d6] px-2.5 py-0.5 text-xs font-semibold text-[#7a4a00]">{receipt.refund.status === 'refunded' ? 'Refunded' : `${formatMoney(receipt.refund.amount)} refunded`}</span>}
    </span>
    <span className="flex shrink-0 items-center gap-2">
      <span className="text-[15px] font-semibold tabular-nums">{formatMoney(receipt.amountPaid)}</span>
      <ChevronRight aria-hidden="true" className="h-5 w-5 text-[var(--portal-muted)] transition group-hover:translate-x-0.5" />
    </span>
  </Link>;
}

export default DtpBilling;
